import { useEffect, useState } from "react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Download, FileSpreadsheet } from "lucide-react";

const downloadFile = async (path, params, filename) => {
  const token = localStorage.getItem("khn_token");
  const url = new URL(`${API_BASE}${path}`);
  Object.entries(params).forEach(([k, v]) => v && url.searchParams.set(k, v));
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  const blob = await res.blob();
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
};


export default function UtangPage() {
  const nowY = new Date().getFullYear();
  const [tahun, setTahun] = useState(nowY);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const empty = { tahun: nowY, pemberi_pinjaman: "", nama: "", tahun_peminjaman: nowY, nilai: 0, keterangan: "" };
  const [form, setForm] = useState(empty);

  const load = async () => { const r = await api.get("/utang", { params: { tahun } }); setItems(r.data); };
  useEffect(() => { load(); }, [tahun]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));
  const openNew = () => { setEditId(null); setForm({ ...empty, tahun }); setOpen(true); };
  const openEdit = (it) => { setEditId(it.id); setForm({ ...empty, ...it }); setOpen(true); };

  const save = async () => {
    if (!form.pemberi_pinjaman?.trim()) { toast.error("Pemberi pinjaman wajib diisi"); return; }
    const payload = { ...form, tahun: Number(form.tahun) || tahun, tahun_peminjaman: Number(form.tahun_peminjaman) || null, nilai: Number(form.nilai) || 0 };
    try {
      if (editId) await api.put(`/utang/${editId}`, payload);
      else await api.post("/utang", payload);
      toast.success(editId ? "Diperbarui" : "Utang tersimpan"); setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delOne = async (id) => { if (!confirm("Hapus utang ini?")) return; await api.delete(`/utang/${id}`); toast.success("Terhapus"); load(); };
  const totalNilai = items.reduce((s, x) => s + Number(x.nilai || 0), 0);

  return (
    <>
      <PageHeader title="Daftar Utang" subtitle="Daftar kewajiban / utang akhir tahun untuk lampiran SPT 1770." actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadFile("/export/utang", { fmt: "pdf", tahun }, `lampiran-utang-${tahun}.pdf`)} className="rounded-sm" data-testid="utang-pdf"><Download size={14} className="mr-1" /> PDF</Button>
          <Button variant="outline" onClick={() => downloadFile("/export/utang", { fmt: "xlsx", tahun }, `lampiran-utang-${tahun}.xlsx`)} className="rounded-sm" data-testid="utang-xlsx"><FileSpreadsheet size={14} className="mr-1" /> Excel</Button>
          <Button onClick={openNew} className="brand-btn rounded-sm" data-testid="add-utang-btn"><Plus size={14} className="mr-1" /> Tambah</Button>
        </div>
      } />
      <div className="p-4 md:p-8 space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="card-flat p-4"><Label>Tahun Pajak</Label><Input type="number" className="w-32" value={tahun} onChange={(e) => setTahun(Number(e.target.value) || tahun)} data-testid="utang-tahun" /></div>
          <div className="card-flat p-4"><div className="text-[11px] uppercase tracking-widest text-slate-500">Total Utang {tahun}</div><div className="mt-1 font-mono text-2xl text-rose-700">{rupiah(totalNilai)}</div></div>
        </div>
        <div className="card-flat overflow-x-auto">
          <table className="w-full text-sm" data-testid="utang-table">
            <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="p-3">Pemberi Pinjaman</th><th className="p-3">Keterangan</th><th className="p-3">Th. Peminjaman</th><th className="p-3 text-right">Nilai</th><th className="p-3 text-right">Aksi</th>
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="5" className="text-center py-10 text-slate-400">Belum ada utang.</td></tr>}
              {items.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-3">{p.pemberi_pinjaman}</td>
                  <td className="p-3">{p.nama || "-"}<div className="text-[11px] text-slate-400">{p.keterangan}</div></td>
                  <td className="p-3">{p.tahun_peminjaman || "-"}</td>
                  <td className="p-3 text-right font-mono font-semibold">{rupiah(p.nilai)}</td>
                  <td className="p-3"><div className="flex items-center gap-1 justify-end">
                    <button onClick={() => openEdit(p)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-utang-${p.id}`}><Pencil size={14} /></button>
                    <button onClick={() => delOne(p.id)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-utang-${p.id}`}><Trash2 size={14} className="text-rose-600" /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="utang-dialog">
          <DialogHeader><DialogTitle>{editId ? "Ubah" : "Tambah"} Utang</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Tahun Pajak</Label><Input type="number" value={form.tahun} onChange={upd("tahun")} /></div>
            <div className="space-y-1.5"><Label>Tahun Peminjaman</Label><Input type="number" value={form.tahun_peminjaman || ""} onChange={upd("tahun_peminjaman")} /></div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Pemberi Pinjaman <span className="text-rose-500">*</span></Label><Input value={form.pemberi_pinjaman} onChange={upd("pemberi_pinjaman")} placeholder="mis. Bank Mandiri / Koperasi" data-testid="utang-pemberi" /></div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Keterangan Utang</Label><Input value={form.nama} onChange={upd("nama")} placeholder="mis. KPR / Kredit kendaraan" /></div>
            <div className="space-y-1.5"><Label>Nilai (Rp)</Label><CurrencyInput value={form.nilai} onValueChange={upd("nilai")} /></div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Catatan</Label><Textarea value={form.keterangan} onChange={upd("keterangan")} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Batal</Button><Button onClick={save} className="brand-btn rounded-sm" data-testid="utang-save">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
